import React from 'react';
import { muiLightTheme, muiDarkTheme } from './options/mui-theme';
import {
  ThemeProviderProps,
  ThemeProvider,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { unifiedLightTheme } from './options/unified-theme/light';
import { unifiedDarkTheme } from './options/unified-theme/dark';

/**
 * The `appThemeOptions` object contains the configuration options for the themes applied to the application.
 * It defines the `unifiedTheme` and `muiTheme` properties, each with `light` and `dark` variants.
 * This object is used by the `ThemeProvider` to apply the correct theme based on the user's selection.
 *
 * @category Theming
 */
const appThemeOptions: ThemeProviderProps['options'] = {
  unifiedTheme: {
    light: unifiedLightTheme,
    dark: unifiedDarkTheme,
  },
  muiTheme: {
    light: muiLightTheme,
    dark: muiDarkTheme,
  },
};

/**
 * `AppThemeProvider` is a component that provides the theming context to the application.
 * It wraps the children components with the appropriate theme configuration using the `ThemeProvider` component.
 * The theme is determined based on the `appThemeOptions` configuration.
 * The `activeTheme` prop specifies whether the light or dark theme should be applied.
 *
 * @param {React.ReactNode} children - The child components that will be wrapped by the theme provider.
 *
 * @returns {React.ReactElement} The themed children components.
 *
 * @category Theming
 * @document Theming in Backstage Application.md
 */
export const AppThemeProvider = ({
  children,
  activeTheme,
}: {
  children: React.ReactNode;
  activeTheme?: 'light' | 'dark';
}): React.ReactElement => {
  return (
    <ThemeProvider options={{ ...appThemeOptions, activeTheme }}>
      {children}
    </ThemeProvider>
  );
};
